import React from 'react';
import { Box } from '@chakra-ui/react';
import MDEditor, { commands } from '@uiw/react-md-editor';
import '@uiw/react-md-editor/markdown-editor.css';

// Reusable Markdown editor (replaces the old ReactQuill HTML editor).
//
// The body is authored and stored as Markdown — not HTML — so that rich-text content
// clears the upstream WAF, which flags embedded HTML tags/links as attacks. What you
// type here is exactly what is stored; there is no lossy HTML<->Markdown conversion.
//
// Optional inline image upload: pass `uploadImage(file) => Promise<url>` to add a
// toolbar button that uploads the chosen file and inserts a Markdown image at the
// cursor. This preserves the old Quill "insert image" behaviour.
const MarkdownEditor = ({ value, onChange, height = 260, uploadImage }) => {
    const imageUploadCommand = uploadImage && {
        name: 'upload-image',
        keyCommand: 'upload-image',
        buttonProps: { 'aria-label': 'Upload image', title: 'Upload image' },
        icon: (
            <svg width="12" height="12" viewBox="0 0 20 20">
                <path fill="currentColor" d="M15 9c1.1 0 2-.9 2-2s-.9-2-2-2-2 .9-2 2 .9 2 2 2zM4 3h12a2 2 0 0 1 2 2v10a2 2 0 0 1-2 2H4a2 2 0 0 1-2-2V5a2 2 0 0 1 2-2zm0 12h12v-2.5l-3.5-3.5-3 3-2-2L4 12.5V15z"/>
            </svg>
        ),
        execute: (state, api) => {
            const input = document.createElement('input');
            input.setAttribute('type', 'file');
            input.setAttribute('accept', 'image/*');
            input.onchange = async () => {
                const file = input.files && input.files[0];
                if (!file) return;
                try {
                    const url = await uploadImage(file);
                    if (url) api.replaceSelection(`![image](${url})`);
                } catch (err) {
                    // The caller's uploadImage is expected to surface its own error UI.
                    console.error('Image upload failed:', err);
                }
            };
            input.click();
        },
    };

    const editorCommands = uploadImage
        ? [...commands.getCommands(), commands.divider, imageUploadCommand]
        : undefined;

    return (
        // data-color-mode keeps the editor in light theme to match the admin UI.
        <Box data-color-mode="light">
            <MDEditor
                value={value || ''}
                onChange={(val) => onChange(val || '')}
                height={height}
                preview="edit"
                commands={editorCommands}
                textareaProps={{ placeholder: 'Write the content using Markdown…' }}
            />
        </Box>
    );
};

export default MarkdownEditor;
